import React from 'react'
import { useInView } from 'react-intersection-observer'
import { useTranslation } from 'react-i18next'
import { BookOpen, GraduationCap, Users, Clock, Star } from 'lucide-react'

const Academics = () => {
  const { t } = useTranslation()
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const academicLevels = [
    {
      icon: <BookOpen className="w-12 h-12" />,
      title: t('academics.primary'),
      ageGroup: t('academics.primaryAgeGroup'),
      description: t('academics.primaryDescription'),
      features: [
        t('academics.coreSubjects'),
        t('academics.gujaratiLanguage'),
        t('academics.environmentalStudies'),
        t('academics.physicalEducation'),
        t('academics.computerBasics')
      ],
      color: "from-primary-green to-primary-green/80",
      bgColor: "bg-primary-green/10",
      borderColor: "border-primary-green"
    },
    {
      icon: <GraduationCap className="w-12 h-12" />,
      title: t('academics.secondary'),
      ageGroup: t('academics.secondaryAgeGroup'),
      description: t('academics.secondaryDescription'),
      features: [
        t('academics.gsebCurriculum'),
        t('academics.scienceMath'),
        t('academics.commerceStream'),
        t('academics.artsHumanities'),
        t('academics.careerGuidance')
      ],
      color: "from-primary-blue to-primary-blue/80",
      bgColor: "bg-primary-blue/10",
      borderColor: "border-primary-blue"
    }
  ]

  const highlights = [
    {
      icon: <Users className="w-8 h-8" />,
      title: t('academics.qualifiedFaculty'),
      description: t('academics.qualifiedFacultyDesc')
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: t('academics.flexibleTimings'),
      description: t('academics.flexibleTimingsDesc')
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: t('academics.excellenceRecord'),
      description: t('academics.excellenceRecordDesc')
    }
  ]

  return (
    <section id="academics" className="py-16 lg:py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={ref}
          className={`transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-primary-purple font-poppins mb-10">
              {t('academics.title')}
            </h2>
            <p className="text-3xl uppercase tracking-wide text-primary-purple/90 font-semibold mb-4">
              {t('academics.boardAffiliation')}
            </p>
            <div className="w-24 h-1 bg-primary-yellow mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t('academics.subtitle')}
            </p>
          </div>

          {/* Academic Levels Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {academicLevels.map((level, index) => (
              <div 
                key={index}
                className={`group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 ${level.bgColor} border-2 ${level.borderColor}`}
              >
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${level.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                <div className="relative p-8">
                  {/* Icon */}
                  <div className="text-primary-purple mb-6 transform group-hover:scale-110 transition-transform duration-300">
                    {level.icon}
                  </div>

                  {/* Title and Age Group */}
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-primary-purple mb-2 font-montserrat">
                      {level.title}
                    </h3>
                    <span className="inline-block bg-primary-purple/10 text-primary-purple px-3 py-1 rounded-full text-sm font-semibold">
                      {level.ageGroup}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {level.description}
                  </p>

                  {/* Features */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-primary-purple mb-3">{t('academics.keyFeatures')}</h4>
                    {level.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-primary-purple rounded-full"></div>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Highlights Section */}
          <div className="bg-gradient-to-r from-primary-purple/5 to-primary-blue/5 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-center text-primary-purple mb-12 font-montserrat">
              {t('academics.whyChooseUs')}
            </h3>
            
            <div className="grid md:grid-cols-3 gap-8">
              {highlights.map((highlight, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-lg mb-4 text-primary-purple">
                    {highlight.icon}
                  </div>
                  <h4 className="text-lg font-semibold text-primary-purple mb-2">
                    {highlight.title}
                  </h4>
                  <p className="text-gray-600">
                    {highlight.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Additional Info */}
            <div className="mt-12 text-center">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <div className="text-2xl font-bold text-primary-purple mb-1">15:1</div>
                  <div className="text-sm text-gray-600">{t('academics.studentTeacherRatio')}</div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <div className="text-2xl font-bold text-primary-green mb-1">95%</div>
                  <div className="text-sm text-gray-600">{t('academics.passRate')}</div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <div className="text-2xl font-bold text-primary-blue mb-1">20+</div>
                  <div className="text-sm text-gray-600">{t('academics.extraCurricular')}</div>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <div className="text-2xl font-bold text-primary-yellow mb-1">100%</div>
                  <div className="text-sm text-gray-600">{t('academics.parentSatisfaction')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Academics